import { adminRequest } from "./client";

/**
 * `GET /admin/walletDestinations/{address}` (wallet-service ≥ 0.78.0, any admin): which wallet a
 * Main or Reserve address belongs to, and the owner's current addresses (the M6 check before
 * money is sent to a saved address). 404 for an address that is no account of an Elios wallet,
 * 400 for one that is not an EVM address.
 */
export interface WalletDestination {
  /** The address, EIP-55 checksummed. */
  address: string;
  walletId: string;
  role: "main" | "reserve";
  /** The wallet's state (`provisioned`, `migrated`, `retired`, …). */
  state: string;
  /** The wallet is closed (soft-deleted). */
  deleted: boolean;
  /** The owner's current wallet (`currentForOwner`), or null when none. */
  currentWalletId: string | null;
  currentMainAddress: string | null;
  currentReserveAddress: string | null;
}

export function lookupWalletDestination(
  address: string,
  token: string,
  signal?: AbortSignal,
): Promise<WalletDestination> {
  return adminRequest<WalletDestination>({
    method: "GET",
    path: `/admin/walletDestinations/${encodeURIComponent(address)}`,
    token,
    signal,
  });
}

export type DestinationVerdict = "current" | "previous" | "closed" | "noCurrent" | "other";

/**
 * What a sender should be told about money sent to the address: it lands on the owner's current
 * wallet; on a previous one (forwarded while a forward key lives, else the owner's Move); on a
 * closed one (nothing forwards: an operator recovers it); or the owner has no current wallet.
 */
export function destinationVerdict(d: WalletDestination): {
  verdict: DestinationVerdict;
  note: string;
} {
  if (d.deleted)
    return {
      verdict: "closed",
      note: "The wallet is closed: money sent here is not forwarded. An operator must recover it. Give the sender the current address instead.",
    };
  if (d.currentWalletId === d.walletId)
    return {
      verdict: "current",
      note: "The owner's current wallet: money sent here lands as expected.",
    };
  if (!d.currentWalletId) return { verdict: "noCurrent", note: "The owner has no current wallet." };
  if (d.state === "migrated" || d.state === "retired")
    return {
      verdict: "previous",
      note: `A previous (${d.state}) wallet: money sent here is forwarded to the current wallet while the migration's forward key lives (90 days after completion), then only by the owner's Move. Give the sender the current ${d.role} address.`,
    };
  return {
    verdict: "other",
    note: `Not the owner's current wallet (it is ${d.state}). Give the sender the current ${d.role} address.`,
  };
}
